import React, { useCallback, useContext, useEffect, useState } from 'react';
import Modal from '../general/Modal';
import { UserContext } from '../ContextProviders';
import {
  buildNovaDocumentKey,
  isAllowedNovaDocument,
  listNovaDocuments,
  NOVA_DOCUMENT_ACCEPT,
  NOVA_DOCUMENT_FORMATS_LABEL,
  NOVA_DOCUMENT_LIMITS_LABEL,
  NOVA_DOCUMENT_MAX_BYTES,
  NOVA_DOCUMENT_MAX_FILES,
  NOVA_IMAGE_MAX_BYTES,
  novaDocumentMaxBytes,
  uploadNovaDocument,
} from '@/api';
import { useNotification } from '@/components';
import { NovaDocumentObject } from '@/types';
import XLight from '@/assets/xLight.svg';

interface UploadItem {
  name: string;
  // Full object key this file uploads to — see buildNovaDocumentKey.
  key: string;
  // The original filename, unmodified — the trailing segment of `key`, and the
  // string sent to the chat payload's file_paths once the upload completes. No
  // uuid: re-uploading the same name overwrites the previous object.
  relativeName: string;
  fileItem: File;
  status: 'uploading' | 'complete' | 'error';
  bytesUploaded: number;
}

interface NovaUploadDialogProps {
  open: boolean;
  userProfileId: string;
  onClose: () => void;
  onUploaded: (filePaths: string[]) => void;
  // Pre-fills the file list on open — lets a caller's own drop zone (e.g. the
  // Documents page) hand off files a user already dragged there, skipping the
  // reselect step this dialog's own drop zone would otherwise require.
  initialFiles?: File[];
}

const formatBytes = (bytes: number) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
};

const NovaUploadDialog: React.FC<NovaUploadDialogProps> = ({ open, userProfileId, onClose, onUploaded, initialFiles }) => {
  const user = useContext(UserContext);
  const aadGuid = user?.userId || '';
  const { showNotification } = useNotification();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Every way files reach this dialog (picker, drop, a caller's initialFiles) goes
  // through here — the `accept` filter alone is bypassable by drag-and-drop and by
  // the picker's "All files" option.
  const keepSupportedFiles = (files: File[]): File[] => {
    const supported = files.filter(isAllowedNovaDocument);
    const wrongType = files.length - supported.length;
    if (wrongType > 0) {
      showNotification(
        `${wrongType > 1 ? `${wrongType} files were` : 'A file was'} skipped — only ${NOVA_DOCUMENT_FORMATS_LABEL} files are supported.`,
        'error',
      );
    }

    const withinLimit = supported.filter((file) => file.size <= novaDocumentMaxBytes(file));
    const tooLarge = supported.length - withinLimit.length;
    if (tooLarge > 0) {
      showNotification(
        `${tooLarge > 1 ? `${tooLarge} files were` : 'A file was'} skipped — images must be ${formatBytes(NOVA_IMAGE_MAX_BYTES)} or smaller, documents ${formatBytes(NOVA_DOCUMENT_MAX_BYTES)} or smaller.`,
        'error',
      );
    }
    return withinLimit;
  };

  const capFileCount = (files: File[], alreadySelected: number): File[] => {
    const room = Math.max(NOVA_DOCUMENT_MAX_FILES - alreadySelected, 0);
    if (files.length > room) {
      showNotification(`You can upload up to ${NOVA_DOCUMENT_MAX_FILES} files at a time.`, 'error');
    }
    return files.slice(0, room);
  };

  // Callers close the dialog after a successful upload by flipping `open` off
  // directly (not via handleClose), so reset() below never runs for them —
  // without also clearing here, reopening would show the previous upload's
  // stale "Uploading.../Done" screen instead of a fresh picker.
  useEffect(() => {
    if (open) {
      setSelectedFiles(initialFiles && initialFiles.length > 0 ? capFileCount(keepSupportedFiles(initialFiles), 0) : []);
      setUploads([]);
      setIsUploading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reset = useCallback(() => {
    setSelectedFiles([]);
    setUploads([]);
    setIsUploading(false);
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  // The filename alone is the object key (see buildNovaDocumentKey), so two files
  // sharing a name — even different files picked from different folders — would
  // upload to the same object and the later one would silently replace the earlier.
  // Only the first file of any given name is kept in a batch.
  const addFiles = (files: FileList | File[]) => {
    const incoming = keepSupportedFiles(Array.from(files));
    if (incoming.length === 0) return;
    const seenNames = new Set(selectedFiles.map((file) => file.name));
    const deduped: File[] = [];
    for (const file of incoming) {
      if (seenNames.has(file.name)) continue;
      seenNames.add(file.name);
      deduped.push(file);
    }
    if (deduped.length < incoming.length) {
      showNotification('A file with the same name was already added, so it was skipped.', 'info');
    }
    const accepted = capFileCount(deduped, selectedFiles.length);
    if (accepted.length > 0) setSelectedFiles([...selectedFiles, ...accepted]);
  };

  const startUpload = async () => {
    if (selectedFiles.length === 0) return;
    if (!userProfileId || !aadGuid) {
      showNotification("Nova can't identify your account right now, so files can't be uploaded.", 'error');
      return;
    }
    setIsUploading(true);

    // The store keeps no content hash, so an already-uploaded file is matched by
    // name+size against everything this account has indexed (documents live flat
    // per user, so a match is valid for any chat — no per-session copy needed). A
    // match just reuses its existing relativeName; nothing is uploaded for it.
    let existingDocuments: NovaDocumentObject[] = [];
    try {
      const docsResponse = await listNovaDocuments(aadGuid, userProfileId);
      if (docsResponse.succeeded) existingDocuments = docsResponse.documents ?? [];
    } catch (error) {
      console.error('Error checking existing Nova documents:', error);
    }
    const findExisting = (file: File) => existingDocuments.find((doc) => doc.name === file.name && doc.size === file.size);

    const filesToReuse = selectedFiles.filter((file) => findExisting(file));
    const filesToUpload = selectedFiles.filter((file) => !findExisting(file));

    const reuseItems: UploadItem[] = filesToReuse.map((fileItem) => {
      const existing = findExisting(fileItem)!;
      return {
        name: fileItem.name,
        key: existing.key,
        relativeName: existing.key.split('/').pop() || '',
        fileItem,
        status: 'complete',
        bytesUploaded: fileItem.size,
      };
    });
    // `relativeName` is just the original filename (no uuid) and is what's sent on
    // to the chat payload's file_paths.
    const uploadItems: UploadItem[] = filesToUpload.map((fileItem) => ({
      name: fileItem.name,
      key: buildNovaDocumentKey(userProfileId, fileItem.name),
      relativeName: fileItem.name,
      fileItem,
      status: 'uploading',
      bytesUploaded: 0,
    }));
    setUploads([...reuseItems, ...uploadItems]);

    if (reuseItems.length > 0) {
      showNotification(
        reuseItems.length > 1
          ? `${reuseItems.length} files already exist so will not upload.`
          : 'This file already exists so will not upload.',
        'info',
      );
    }

    const filePaths: string[] = reuseItems.map((item) => item.relativeName);
    for (const item of uploadItems) {
      try {
        await uploadNovaDocument(aadGuid, item.key, item.fileItem, (bytesUploaded) => {
          setUploads((prev) => prev.map((u) => (u.name === item.name ? { ...u, bytesUploaded } : u)));
        });
        filePaths.push(item.relativeName);
        setUploads((prev) => prev.map((u) => (u.name === item.name ? { ...u, status: 'complete' } : u)));
      } catch {
        setUploads((prev) => prev.map((u) => (u.name === item.name ? { ...u, status: 'error' } : u)));
      }
    }

    onUploaded(filePaths);
  };

  const allComplete = uploads.length > 0 && uploads.every((u) => u.status !== 'uploading');

  return (
    <Modal
      open={open}
      onClose={handleClose}
      // Only block backdrop-dismiss while a transfer is actually in flight, so an
      // accidental outside click can't drop an in-progress upload — otherwise let
      // clicking outside close it like any other dialog.
      disableCloseOnBackdropClick={isUploading}
      zIndex={1000000}
      contanerClassName="w-[480px] max-w-[90vw] bg-white shadow-xl"
    >
      <div className="flex items-center justify-between p-4">
        <h3 className="text-lg font-medium text-gray-900">Add documents to this chat</h3>
        <XLight className="h-6 w-6 cursor-pointer" onClick={handleClose} />
      </div>

      <div className="p-6 pt-0">
        {!isUploading && (
          <>
            <div
              className={`mb-4 flex h-[140px] w-full items-center justify-center rounded border-2 border-dashed transition-colors ${
                isDragOver ? 'border-gray-400 bg-gray-50' : 'border-gray-300'
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
              }}
            >
              <p className="text-center text-sm text-gray-600">
                Drag and drop documents here
                <span className="mt-1 block text-xs text-gray-500">{NOVA_DOCUMENT_FORMATS_LABEL}</span>
                <span className="mt-1 block text-xs text-gray-500">{NOVA_DOCUMENT_LIMITS_LABEL}</span>
              </p>
            </div>

            <label className="block cursor-pointer border-b border-black pb-1 text-sm text-gray-600">
              or click here to choose files
              <input
                type="file"
                multiple
                accept={NOVA_DOCUMENT_ACCEPT}
                className="hidden"
                onChange={(e) => e.target.files && addFiles(e.target.files)}
              />
            </label>

            {selectedFiles.length > 0 && (
              <div className="mt-4 max-h-32 space-y-2 overflow-y-auto" data-lenis-prevent>
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between rounded border p-2 text-sm">
                    <span className="truncate">
                      {file.name} <span className="text-gray-500">({formatBytes(file.size)})</span>
                    </span>
                    <XLight
                      className="h-4 w-4 cursor-pointer text-red-500"
                      onClick={() => setSelectedFiles((prev) => prev.filter((_, i) => i !== index))}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {uploads.length > 0 && (
          <div className="mt-4 space-y-2">
            {uploads.map((item) => (
              <div key={item.name} className="rounded border p-2 text-sm">
                <div className="mb-1 flex justify-between">
                  <span className="truncate">{item.name}</span>
                  <span className="capitalize">{item.status}</span>
                </div>
                <div className="h-1.5 w-full rounded bg-gray-200">
                  <div
                    className="h-full rounded bg-black transition-all"
                    style={{ width: `${(item.bytesUploaded * 100) / (item.fileItem.size || 1)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 bg-gray-50 p-4">
        {!isUploading ? (
          <>
            <button
              onClick={startUpload}
              disabled={selectedFiles.length === 0}
              className={`h-10 min-w-[71px] rounded-3xl px-4 py-2 ${
                selectedFiles.length === 0 ? 'cursor-not-allowed bg-[#f5f5f5] text-gray-500' : 'cursor-pointer bg-black text-white'
              }`}
            >
              Upload
            </button>
            <button onClick={handleClose} className="h-10 cursor-pointer rounded-3xl bg-[#f5f5f5] px-4 py-2 text-gray-700">
              Cancel
            </button>
          </>
        ) : (
          <button
            onClick={handleClose}
            disabled={!allComplete}
            className={`h-10 min-w-[90px] rounded-3xl px-4 py-2 ${
              allComplete ? 'cursor-pointer bg-black text-white' : 'cursor-not-allowed bg-[#f5f5f5] text-gray-500'
            }`}
          >
            {allComplete ? 'Done' : 'Uploading...'}
          </button>
        )}
      </div>
    </Modal>
  );
};

export default NovaUploadDialog;
